import {AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {StorageService} from "../../../../../projects/vendors/src/lib/services/storage.service";
import {ApiEnum} from "../../../@application/enum/api.enum";
import {ApiBaseService} from "../../../../../projects/vendors/src/lib/services/api-base.service";
import {Chart, ArcElement, Tooltip, Legend} from "chart.js";
import {forkJoin, map, of, shareReplay, switchMap, tap} from "rxjs";
import {RestaurantTable} from "../../../@domain/restaurent-table";
import {TodayOrder} from "../../../@domain/today-order";
import {RestaurantDetail} from "../../../@domain/restaurant-config";
import {RestaurantConfig} from "../../../../../projects/settings/src/lib/@domain/restaurant-config";
import {SettingsApiEnum} from "../../../../../projects/settings/src/lib/@application/enums/settings-api-enum";
import {catchError} from "rxjs/operators";
import {BrandingService} from "../../../@application/services/branding.service";

interface TableSummary {
  total: number;
  available: number;
  occupied: number;
}

interface OrderSummary {
  total: number;
  ongoing: number;
  completed: number;
  completionRate: number; // 0-100
}

/** `dashboard/revenue-profit` (no dates) returns today's bills grouped by order type. */
interface OrderTypeRevenue {
  orderType: number;
  totalOrders: number;
  totalRevenue: number;
  totalProfit: number;
}

interface RecentOrder {
  id: string;
  minutesAgo: number;
  status: 'In Progress' | 'Completed';
}

Chart.register(ArcElement, Tooltip, Legend);

@Component({
    selector: 'app-dash-board',
    templateUrl: './dash-board.component.html',
    styleUrl: './dash-board.component.scss',
    standalone: false
})
export class DashBoardComponent implements OnInit, AfterViewInit, OnDestroy {


  private storageService = inject(StorageService)
  apiBaseService = inject(ApiBaseService);
  private brandingService = inject(BrandingService);
  @ViewChild('tableMgmtDonut', {static: false}) donutRef!: ElementRef<HTMLCanvasElement>;
  private donutChart?: Chart;
  toDay = new Date()

  startedTime!: Date;
  endTime!: Date;

  tableSummary = {} as TableSummary;
  restaurantDetails = {} as RestaurantDetail
  orderSummary = {} as OrderSummary;

  todaySales = 0;
  todayBills = 0;
  /** The endpoint is permission-gated server-side; skip the call rather than eat a 403 toast. */
  canViewSales = ((this.storageService.getFromSessionStorage('permissions') as string[]) ?? [])
    .includes('rpos:pos:order-bill:view-revenue-profit');

  tables: RestaurantTable[] = [];

  recentOrders: RecentOrder[] = [];

  ngOnInit() {
    this.loadAllData()
  }

  private setDefaultValues(currentDate: Date) {

    const startTimeParts = this.restaurantDetails.dayEndInfo.startTimeIn24H.split(':').map(Number);
    this.startedTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), ...startTimeParts);

    const endTimeParts = this.restaurantDetails.dayEndInfo.endTimeIn24H.split(':').map(Number);
    let closedTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), ...endTimeParts);

    if (!this.restaurantDetails.dayEndInfo.endInSameDay) {
      closedTime.setDate(closedTime.getDate() + 1);
    }
    this.endTime = closedTime;
  }

  // Formats a LOCAL wall-clock Date without a timezone suffix so the backend receives an
  // Unspecified-kind value and converts it via the restaurant's own timezone. `.toISOString()`
  // would UTC-shift startedTime/endTime by the local offset before the backend ever sees them.
  private toLocalIsoString(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  ngAfterViewInit(): void {

  }

  ngOnDestroy(): void {
    this.donutChart?.destroy();
  }

  private renderDonut(): void {
    const available = this.tableSummary.available;
    const occupied = this.tableSummary.occupied;

    this.donutChart?.destroy();
    this.donutChart = new Chart(this.donutRef.nativeElement.getContext('2d')!, {
      type: 'doughnut',
      data: {
        labels: ['Available', 'Occupied'],
        datasets: [
          {
            data: [available, occupied],
            backgroundColor: [this.brandingService.getBrandColor(), '#FF8A00'],
            hoverBackgroundColor: [this.brandingService.getBrandColorAlpha(0.8), '#e67c00'],
            borderWidth: 0
          }
        ]
      },
      options: {
        responsive: true,
        cutout: '70%',
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.label}: ${ctx.parsed} tables`
            }
          }
        }
      }
    });
  }

  // Call this if data changes from API
  refreshData(
    tableSummary: TableSummary,
    orderSummary: OrderSummary,
    tables: RestaurantTable[],
    recentOrders: RecentOrder[]
  ) {
    this.tableSummary = tableSummary;
    this.orderSummary = orderSummary;
    this.tables = tables;
    this.recentOrders = recentOrders;
    this.renderDonut();
  }
  onRefresh() {
    this.loadAllData();
  }

  private getOrFetchRestaurantDetails$() {
    const cached = this.storageService.getFromSessionStorage('restaurantDetail');
    if (cached) {
      return of(cached);
    }
    return this.apiBaseService
      .GET<RestaurantConfig>([ApiEnum.RestaurantModule, ApiEnum.SystemSetting, ApiEnum.RestaurantDetail])
      .pipe(
        tap(cfg => this.storageService.saveToSessionStorage('restaurantDetail', cfg.restaurantDetail)),
        map(cfg => cfg.restaurantDetail),           // <-- return only restaurantDetail
        shareReplay(1)                              // optional: cache result for this session
      );
  }

  private loadAllData(): void {
    this.getOrFetchRestaurantDetails$()
      .pipe(
        tap(details => {
          this.restaurantDetails = details;
          if (details?.dayEndInfo) {
            this.setDefaultValues(this.toDay);
          } else {
            this.setDefaultValues(this.toDay);
          }
        }),
        switchMap(() => {
          const tableRequest = this.apiBaseService.GET<RestaurantTable[]>(
            [ApiEnum.RestaurantModule, ApiEnum.RestaurantTable]
          );

          const todayOrderRequest = this.apiBaseService.GET<TodayOrder[]>(
            [ApiEnum.RestaurantModule, ApiEnum.Order, ApiEnum.Today_Orders,this.toLocalIsoString(this.startedTime),this.toLocalIsoString(this.endTime)]
          );

          const todaySalesRequest = this.canViewSales
            ? this.apiBaseService.GET<OrderTypeRevenue[]>(
                [ApiEnum.RestaurantModule, ApiEnum.OrderBill, ApiEnum.DashBoard, ApiEnum.RevenueProfit]
              )
            : of([] as OrderTypeRevenue[]);

          return forkJoin({ table: tableRequest, todayOrder: todayOrderRequest, todaySales: todaySalesRequest });
        }),
        catchError(err => {
          console.error('Dashboard load failed', err);
          return of({ table: [], todayOrder: [], todaySales: [] } as
            { table: RestaurantTable[]; todayOrder: TodayOrder[]; todaySales: OrderTypeRevenue[] });
        })
      )
      .subscribe(({ table, todayOrder, todaySales }) => {
        this.todaySales = todaySales.reduce((sum, x) => sum + x.totalRevenue, 0);
        this.todayBills = todaySales.reduce((sum, x) => sum + x.totalOrders, 0);

        this.tables = table;

        this.tableSummary = {
          total: table.length,
          available: table.filter(t => t.availabilityStatus === 1).length,
          occupied: table.filter(t => t.availabilityStatus !== 1).length,
        };

        this.orderSummary = {
          total: todayOrder.length,
          ongoing: todayOrder.filter(x => x.status === 1).length,
          completed: todayOrder.filter(x => x.status === 5).length,
          completionRate: todayOrder.length > 0
            ? Math.round((todayOrder.filter(x => x.status === 5).length / todayOrder.length) * 100)
            : 0,
        };

        this.recentOrders = todayOrder
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 5)
          .map(order => ({
            id: `#${order.orderNumber}`,
            minutesAgo: Math.floor((this.toDay.getTime() - new Date(order.createdAt).getTime()) / 60000),
            status: order.status === 1 ? 'In Progress' : 'Completed'
          }));

        this.renderDonut();
      });
  }
}
